<script setup lang="ts" generic="TStepSchema extends Object, TSchema extends Object">
import { computed } from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VFormButtonsWithFormGroupWidget from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/VFormButtonsWithFormGroupWidget.vue';
import VFormFilterButton from '@modules/form-widgets/components/VFormFilterButton/VFormFilterButton.vue';
import VFormErrorTag from '@modules/forms/components/VFormErrorTag/VFormErrorTag.vue';
import { isNumber } from '@utils/guards';

import type {
  VFormStepsWidgetEmits,
  VFormStepsWidgetProps,
  VFormStepsWidgetStepInner,
} from './types';

const props = withDefaults(defineProps<VFormStepsWidgetProps<TStepSchema, TSchema>>(), {});
const emit = defineEmits<VFormStepsWidgetEmits<TStepSchema, TSchema>>();

const stepGroups = computed<VFormStepsWidgetStepInner<TStepSchema, TSchema>[]>(() => (
  props.steps.map(({ step, value, ...restStep }) => ({
    ...restStep,
    step: {
      buttons: [{
        formSchema: step.formSchema,
        value: step.value,
        type: 'primary',
        valueSuffix: step.valueSuffix,
        label: step.label,
      }],
      formTitle: step.formTitle,
      formSubmitBtn: step.formSubmitBtn,
      formCloseBtn: step.formCloseBtn,
    },
    value: {
      buttons: [{
        formSchema: value.formSchema,
        value: value.value,
        type: 'secondary',
        valueSuffix: value.valueSuffix,
        label: value.label,
      }],
      formTitle: value.formTitle,
      formSubmitBtn: value.formSubmitBtn,
      formCloseBtn: value.formCloseBtn,
    },
  } as VFormStepsWidgetStepInner<TStepSchema, TSchema>))
));

function onStepChange(index: number, value: TStepSchema): void {
  const step = props.steps[index];

  if (step) {
    emit('change', index, value, step.value.value);
  }
}

function onValueChange(index: number, value: TSchema): void {
  const step = props.steps[index];

  if (step) {
    emit('change', index, step.step.value, value);
  }
}

function onStepInput(index: number | string, value: TStepSchema): void {
  if (isNumber(index)) {
    const step = props.steps[index];

    if (step) {
      emit('input', index, value, step.value.value);
    }
  }
}

function onValueInput(index: number | string, value: TSchema): void {
  if (isNumber(index)) {
    const step = props.steps[index];

    if (step) {
      emit('input', index, step.step.value, value);
    }
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'VFormStepsWidget'" class="flex flex-col gap-4">
    <div
      v-for="(step, index) in stepGroups"
      :key="index"
      class="flex gap-4 max-sm:flex-col max-sm:items-center"
    >
      <div
        class="flex items-center gap-4 max-sm:w-full sm:min-w-[450px] md:min-w-[500px]"
      >
        <span class="flex-none pl-3 text-sm text-muted-color">
          {{ step.label }}
        </span>
        <VFormButtonsWithFormGroupWidget
          :buttons="step.step.buttons"
          :form-title="step.step.formTitle"
          :form-close-btn="step.step.formCloseBtn"
          :form-submit-btn="step.step.formSubmitBtn"
          :readonly="readonly"
          @change="(btnIndex, value) => onStepChange(index, value)"
          @input="(btnIndex, value) => onStepInput(index, value)"
        />
        <VFormButtonsWithFormGroupWidget
          :buttons="step.value.buttons"
          :form-title="step.value.formTitle"
          :form-close-btn="step.value.formCloseBtn"
          :form-submit-btn="step.value.formSubmitBtn"
          :readonly="readonly"
          @change="(btnIndex, value) => onValueChange(index, value)"
          @input="(btnIndex, value) => onValueInput(index, value)"
        />

        <VIconButton
          v-if="step.allowRemove && !readonly"
          icon="pi-trash"
          class="ml-auto"
          @click="emit('remove', index)"
        />
      </div>

      <VFormErrorTag
        :errors="step.errors"
      />
    </div>

    <div
      v-if="addLabel || infoText || errors?.length"
      class="flex max-sm:flex-col max-sm:items-center max-sm:gap-3"
    >
      <VMessage
        v-if="infoText"
        severity="info"
      >
        {{ infoText }}
      </VMessage>
      <VFormFilterButton
        v-else-if="addLabel && !readonly"
        :label="addLabel"
        is-clickable
        is-add
        type="transparent"
        class="flex-none"
        rounded
        @click="emit('add')"
      />

      <VFormErrorTag
        :errors="errors"
      />
    </div>
  </div>
</template>
